import { useChatStore } from "../stores/chatStore";
import { maxWaitTimePromise } from "../utils";
import PubNub from "pubnub";

// Shared with the portal's chat: see @shared/utils/chatReadCursor.
import {
  lastMarkedReadAt,
  markChannelRead,
} from "@shared/utils/chatReadCursor";

/** PubNub caps a single message-counts request at 100 channels. */
const MAX_CHANNELS_PER_COUNT = 100;

/**
 * How many failed message-counts requests one scan may spend before it gives
 * up narrowing down the offending channels. A channel-specific rejection is
 * isolated within a handful of requests; a global one (expired PAM token,
 * message persistence off, network fault) fails every request, and without a
 * budget the bisect below would turn one dead scan into ~200 dead requests.
 */
const MAX_FAILED_COUNT_REQUESTS = 12;

/**
 * Channels PubNub rejected this session, so a later scan doesn't re-request
 * them and re-pay the bisect. Cleared on reload, which is also when a fresh
 * PAM token arrives.
 */
const rejectedChannels = new Set();

/**
 * Forget the refusals, because the grant that caused them has changed.
 *
 * A portal's token grants `channel-{oem}-{its own connection}-*`, but a rerouted
 * ticket's channel is named after the company that RAISED it — so the moment a
 * portal starts receiving, it holds memberships on channels its current token
 * cannot read. PubNub fails a counts request as a unit, so those channels take
 * the whole batch down with them, and every one of them lands here.
 *
 * They must not stay here. A rejection is a statement about one token, and the
 * backend mints a new one precisely when the routing changes; without this the
 * channels stay excluded from every later scan, the scan stays `isPartial`, and
 * a partial scan MERGES rather than replaces — so nothing the user has lost is
 * ever pruned either. Both halves of the badge stay wrong until a page reload.
 */
export const clearRejectedChannels = () => rejectedChannels.clear();

/**
 * Channels an Access Manager 403 names as forbidden, when it names them.
 * PubNub returns `payload.channels` for a PAM denial, which lets a scan drop
 * exactly the ungranted channels instead of bisecting to find them.
 */
const forbiddenChannelsOf = (error) => {
  const payload =
    error?.status?.errorData?.payload || error?.errorData?.payload || {};
  const channels = [
    ...(payload.channels || []),
    // A denial can name channel groups instead; those carry no counts here.
    ...(payload["channel-groups"] || []),
  ];

  return channels.filter((channel) => typeof channel === "string");
};

const logCountFailure = (error, channels) => {
  const status = error?.status || {};
  // The SDK's message is the generic "REST API request processing error, check
  // status for details" whenever it can't lift a message out of the response
  // body — which is exactly the PAM/history rejections that matter here — so
  // the status code and the channels of the failed request are the only
  // diagnosis available. Log them.
  console.error(
    `[unread] messageCounts failed (status ${status.statusCode ?? "?"}, ${
      channels.length
    } channel(s))`,
    { channels, error },
  );
};

/**
 * Pub nub hook.
 *
 * @param {PubNub} instance Chat instance from pub nub.
 */
const usePubnubInstance = (instance) => {
  const setUnreadChannelMessages = useChatStore(
    (s) => s.setUnreadChannelMessages,
  );
  const mergeUnreadChannelMessages = useChatStore(
    (s) => s.mergeUnreadChannelMessages,
  );
  const removeUnreadChannels = useChatStore((s) => s.removeUnreadChannels);
  const seedUnreadChannels = useChatStore((s) => s.seedUnreadChannels);
  const setAllMembershipChannelsCoil = useChatStore(
    (s) => s.setAllMembershipChannels,
  );

  if (!instance)
    return () => ({
      getAllUnreadMessageCount: () => {},
      updateSingleMembership: () => {},
      refreshChannelsUnread: () => {},
      seedMemberChannels: () => {},
    });

  // A rejected message-counts request used to reject this whole promise chain:
  // nothing was ever written to the store, so every badge in the app — ticket
  // list and sidebar alike — read zero, and the only trace was an uncaught
  // PubNubError. PubNub fails the request as a unit, so a single channel the
  // PAM token doesn't grant read on (a stale membership, an internal-notes
  // channel a portal contact is a member of but can't read) took all 100
  // channels of its chunk down with it — which is why this only ever showed up
  // on some accounts. Resolve with a flag instead and let the caller narrow.
  const getMessagesCounts = async (chans, timeTokens) => {
    try {
      const res = await Promise.race([
        maxWaitTimePromise(),
        instance.messageCounts({
          channels: chans,
          channelTimetokens: timeTokens,
        }),
      ]);

      if (res.isTimeOut) return { channels: {}, isTimeOut: true };

      return res;
    } catch (error) {
      logCountFailure(error, chans);

      return { channels: {}, isError: true, error };
    }
  };

  /**
   * Count a batch of channels, isolating the ones PubNub refuses.
   *
   * On a rejection: drop the channels an Access Manager denial names outright,
   * otherwise bisect the batch to find them. Channels that fail alone are
   * remembered and left out of `channels` in the result, so the caller can
   * tell "counted as 0" from "never counted" and avoid clearing a live badge.
   */
  const countChannels = async (chans, timeTokens, budget) => {
    if (!chans.length) return { channels: {}, isPartial: false };

    const res = await getMessagesCounts(chans, timeTokens);

    if (res?.isTimeOut) return { channels: {}, isPartial: true };
    if (!res?.isError)
      return { channels: res?.channels || {}, isPartial: false };

    budget.failures += 1;
    if (budget.failures >= MAX_FAILED_COUNT_REQUESTS)
      return { channels: {}, isPartial: true };

    // A single channel is as narrow as this gets: it is the bad one.
    if (chans.length === 1) {
      rejectedChannels.add(chans[0]);
      return { channels: {}, isPartial: true };
    }

    const denied = forbiddenChannelsOf(res.error).filter((channel) =>
      chans.includes(channel),
    );
    if (denied.length && denied.length < chans.length) {
      denied.forEach((channel) => rejectedChannels.add(channel));
      const kept = chans
        .map((channel, index) => [channel, timeTokens[index]])
        .filter(([channel]) => !denied.includes(channel));

      const retry = await countChannels(
        kept.map(([channel]) => channel),
        kept.map(([, timeToken]) => timeToken),
        budget,
      );

      return { channels: retry.channels, isPartial: true };
    }

    const mid = Math.ceil(chans.length / 2);
    const left = await countChannels(
      chans.slice(0, mid),
      timeTokens.slice(0, mid),
      budget,
    );
    const right = await countChannels(
      chans.slice(mid),
      timeTokens.slice(mid),
      budget,
    );

    return {
      channels: { ...left.channels, ...right.channels },
      isPartial: true,
    };
  };

  const getChannelsUnreadMessagesCount = async (
    channels,
    channelTimetokens,
  ) => {
    let res = {};
    let isPartial = false;
    const budget = { failures: 0 };
    const slicedChannels = [];
    // Channels PubNub already refused this session stay out of the request:
    // re-sending one only costs another failed round of narrowing.
    const scannable = channels
      .map((channel, index) => [channel, channelTimetokens[index]])
      .filter(([channel]) => !rejectedChannels.has(channel));
    const scanChannels = scannable.map(([channel]) => channel);
    const scanTimetokens = scannable.map(([, timeToken]) => timeToken);
    if (rejectedChannels.size) isPartial = true;

    while (scanChannels.length > 0) {
      const currentLength = Math.min(
        scanChannels.length,
        MAX_CHANNELS_PER_COUNT,
      );
      slicedChannels.push({
        chans: scanChannels.splice(0, currentLength),
        timeTokens: scanTimetokens.splice(0, currentLength),
      });
    }
    for (let chanSliced of slicedChannels) {
      const channelChunks = await countChannels(
        chanSliced.chans,
        chanSliced.timeTokens,
        budget,
      );
      if (channelChunks.isPartial) isPartial = true;
      res = {
        ...res,
        ...channelChunks.channels,
      };
    }

    // A wholesale replace is what prunes channels the user no longer has a
    // membership on — but only a complete scan may prune. When a chunk timed
    // out or was refused its channels are simply missing from `res`, and
    // replacing would drop them from the map entirely; since the live increment
    // path only bumps channels the map already knows, they would then read zero
    // for the rest of the session. Merge instead and leave the previous counts
    // standing.
    if (isPartial) mergeUnreadChannelMessages(res);
    else setUnreadChannelMessages({ channels: res });
  };

  // Persist the read cursor unconditionally. This used to run only when the
  // in-store count was non-zero, so a chat read while its badge already showed
  // zero never advanced `lastReadTimetoken` server-side — and the next recount,
  // which counts from that server-side cursor, resurrected messages the user
  // had already read.
  //
  // Only ever moves an existing cursor, never joins a channel (ENG-1787); see
  // markChannelRead in @shared/utils/chatReadCursor.
  const updateSingleMembership = (channel) =>
    markChannelRead(instance, channel);

  const getChannelMemberships = async (next) => {
    try {
      const res = await Promise.race([
        maxWaitTimePromise(),
        instance.objects.getMemberships({
          page: {
            next,
          },
          include: {
            customFields: true,
            customChannelFields: true,
            totalCount: true,
          },
        }),
      ]);

      return res;
    } catch (error) {
      console.error("[unread] getMemberships failed", { next, error });

      return { isError: true };
    }
  };

  const getAllChannelMemberships = async () => {
    let getResChannels = [];
    let getRes = await getChannelMemberships();
    if (getRes?.data) {
      getResChannels = [...getResChannels, ...getRes.data];
      // `next` is what actually advances the cursor: a page that returns no
      // rows, or none at all, ends the scan. Trusting `totalCount` alone spun
      // forever — re-requesting the same page — whenever it outran the rows
      // PubNub was willing to return.
      while (
        getRes?.next &&
        getRes?.data?.length &&
        getRes?.totalCount > getResChannels.length
      ) {
        getRes = await getChannelMemberships(getRes.next);
        if (!getRes?.data?.length) break;
        getResChannels = [...getResChannels, ...getRes.data];
      }
    }

    return getResChannels;
  };

  // method to get message count for all channels
  const getAllUnreadMessageCount = async () => {
    const allChannels = await getAllChannelMemberships();

    // No memberships read means no information, not "no channels". Replacing
    // the membership coil with an empty map here blanks the sidebar buckets,
    // which are keyed off it.
    if (!allChannels.length) return;

    const notificationChans = [];
    const timetokens = [];
    const allChannelsCoil = {};

    allChannels.map((channel) => {
      const id = channel.channel.id;
      const lastReadTimeToken = channel.custom?.lastReadTimetoken;

      allChannelsCoil[id] = channel.channel;
      notificationChans.push(id);

      if (lastReadTimeToken) {
        timetokens.push((lastReadTimeToken / 10000 + 1) * 10000);
      } else {
        timetokens.push(new Date().getTime() * 10000);
      }
    });

    setAllMembershipChannelsCoil(allChannelsCoil);
    getChannelsUnreadMessagesCount(notificationChans, timetokens);
  };

  // Targeted refresh for the channels whose membership changed. A ticket has
  // two channels (customer-facing + internal-notes) and both must be refreshed
  // together so the unread badge recomputes from fresh assignees/followers.
  // Replaces a full re-pagination of every membership (the App Context spike)
  // with exactly one getMemberships read + one messageCounts read.
  const refreshChannelsUnread = async (channelIds) => {
    const ids = (Array.isArray(channelIds) ? channelIds : [channelIds]).filter(
      Boolean,
    );
    if (!ids.length) return;

    const startedAt = Date.now();

    let res;
    try {
      res = await Promise.race([
        maxWaitTimePromise(),
        instance.objects.getMemberships({
          filter: ids.map((id) => `channel.id == "${id}"`).join(" || "),
          include: {
            customFields: true,
            customChannelFields: true,
            totalCount: true,
          },
          limit: ids.length,
        }),
      ]);
    } catch (error) {
      console.error("[unread] getMemberships (targeted) failed", {
        ids,
        error,
      });

      return;
    }

    if (res?.isTimeOut) return;

    const membershipByChannel = (res?.data || []).reduce((acc, membership) => {
      acc[membership.channel.id] = membership;
      return acc;
    }, {});

    const coil = { ...useChatStore.getState().allMembershipChannels };

    const channelsToCount = [];
    const timetokens = [];
    const removedChannels = [];

    ids.forEach((channelId) => {
      const membership = membershipByChannel[channelId];

      // User was removed from the channel → drop it from both maps.
      if (!membership) {
        delete coil[channelId];
        removedChannels.push(channelId);
        return;
      }

      coil[channelId] = membership.channel;

      const lastReadTimeToken = membership.custom?.lastReadTimetoken;
      channelsToCount.push(channelId);
      timetokens.push(
        lastReadTimeToken
          ? (lastReadTimeToken / 10000 + 1) * 10000
          : new Date().getTime() * 10000,
      );
    });

    setAllMembershipChannelsCoil(coil);
    if (removedChannels.length) removeUnreadChannels(removedChannels);

    if (!channelsToCount.length) return;

    const channelChunks = await countChannels(channelsToCount, timetokens, {
      failures: 0,
    });

    // Merge only the channels this refresh actually counted, and only now that
    // the reads have returned. Writing a map snapshot taken before the awaits
    // would revert everything that landed during them — most visibly the
    // mark-as-read of the very message that triggered this refresh, which is
    // why a chat you were looking at kept its unread badge.
    // A channel missing from the result was never counted — it timed out or
    // PubNub refused it — and defaulting it to 0 would clear a badge that is
    // legitimately non-zero. Only channels PubNub actually returned a count
    // for get written (messageCounts returns an explicit 0 for a read channel).
    const counted = channelsToCount.filter(
      (channelId) =>
        (lastMarkedReadAt.get(channelId) ?? 0) < startedAt &&
        channelChunks?.channels?.[channelId] !== undefined,
    );
    if (!counted.length) return;

    mergeUnreadChannelMessages(
      Object.fromEntries(
        counted.map((channelId) => [
          channelId,
          channelChunks.channels[channelId],
        ]),
      ),
    );
  };

  /**
   * Start tracking the given channels at 0 — but only the ones this user is a
   * member of.
   *
   * For a chat that came into being mid-session (a company assigned after
   * creation), whose membership the last full scan predates. Seeding without
   * asking made any chat the user merely OPENED count toward their badge: an
   * OEM owner reading a ticket a portal has taken over was then badged for
   * every portal message on it, with no membership behind the count.
   */
  const seedMemberChannels = async (channelIds) => {
    const known = useChatStore.getState().unreadChannelMessages.channels;
    const unknown = (channelIds || []).filter(
      (id) => id && known[id] === undefined,
    );
    if (!unknown.length) return;
    try {
      const res = await instance.objects.getMemberships({
        filter: unknown.map((id) => `channel.id == '${id}'`).join(" || "),
        limit: unknown.length,
      });
      const members = (res?.data || [])
        .map((membership) => membership?.channel?.id)
        .filter(Boolean);
      if (members.length) seedUnreadChannels(members);
    } catch (error) {
      console.error("[unread] could not check memberships to seed", {
        channelIds: unknown,
        error,
      });
    }
  };

  return () => {
    return {
      updateSingleMembership, // calling from outside
      getAllUnreadMessageCount,
      refreshChannelsUnread,
      seedMemberChannels,
    };
  };
};

export default usePubnubInstance;
